import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

const ImageReels: FC<{
  images: string[];
  scale?: number;
  gap?: number;
  showBorders?: boolean;
  showPreviews?: boolean;
}> = ({
  images,
  scale = 1, // in rem
  gap = 0.5, // in rem
  showPreviews = false,
  showBorders = false,
}) => {
  const totalSlides = images.length;
  const [isTransitioning, setIsTransitioning] = useState(true);

  const [dynamicAspectRatio, setDynamicAspectRatio] = useState<string>('16/9');

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const SLIDE_WIDTH = showPreviews ? 80 : 100; // percentage of container

  useEffect(() => {
    if (images && images.length > 0) {
      const img = new window.Image();
      img.onload = () => {
        // Once loaded, read the native dimensions and set the CSS aspect-ratio string
        setDynamicAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      };
      img.src = images[0];
    }
  }, [images]);

  // Prepend last image + append first image for infinite loop with peek
  const extendedImages = [images[totalSlides - 1], ...images, images[0]];

  // Start at index 1 (the real first slide) since index 0 is the cloned last image
  const [currentIndex, setCurrentIndex] = useState(1);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }, 5000);
  }, []);

  useEffect(() => {
    if (totalSlides <= 1) return;
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [totalSlides, resetTimer]);

  const handleTransitionEnd = () => {
    // Jumped past the last real slide into the cloned first slide
    if (currentIndex === totalSlides + 1) {
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
    // Jumped before the first real slide into the cloned last slide
    if (currentIndex === 0) {
      setIsTransitioning(false);
      setCurrentIndex(totalSlides);
    }
  };

  const handleDotClick = (index: number) => {
    setIsTransitioning(true);
    setCurrentIndex(index + 1); // +1 because of prepended clone
    resetTimer();
  };

  const activeIndex = (((currentIndex - 1) % totalSlides) + totalSlides) % totalSlides;

  if (totalSlides === 0) return null;

  return (
    <Box sx={{ overflow: 'hidden', position: 'relative', width: '100%' }}>
      <Box
        onTransitionEnd={handleTransitionEnd}
        sx={{
          display: 'flex',
          gap: `${gap}rem`,
          transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
          transform: `translateX(calc(-${currentIndex * SLIDE_WIDTH}% - ${currentIndex * gap}rem + ${(100 - SLIDE_WIDTH) / 2}%))`,
        }}
      >
        {extendedImages.map((image, index) => {
          const isCenter = index === currentIndex;
          return (
            <Box
              key={index}
              sx={{
                flex: `0 0 ${SLIDE_WIDTH}%`,
                maxWidth: `${SLIDE_WIDTH}%`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                transform: isCenter ? `scale(${Number(scale)})` : `scale(${Number(scale - 0.05)})`,
                opacity: isCenter ? 1 : 0.45,
                transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                borderRadius: '16px',
                ...(showPreviews && {
                  aspectRatio: dynamicAspectRatio,
                }),
                ...(showBorders && {
                  border: '10px solid transparent',
                  background:
                    'linear-gradient(#fff, #fff) padding-box, ' +
                    'linear-gradient(to bottom right, #000000, #2D6966) border-box',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                }),
              }}
            >
              <Box
                component="img"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                src={image}
                alt={`Slide ${((((index - 1) % totalSlides) + totalSlides) % totalSlides) + 1}`}
              />
            </Box>
          );
        })}
      </Box>
      {totalSlides > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            bottom: 12,
            width: '100%',
            gap: '8px',
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: activeIndex === index ? '#000' : '#ccc',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ImageReels;
